import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, ReactNode } from 'react';
import { Socket } from 'socket.io-client';
import { KGBuildStatus } from '@/types/knowledgeGraph';
import { useAdminSocket } from './AdminSocketContext';

export interface KGAgentStatusUpdate {
  agent_id: string;
  user_id: string;
  status: {
    kg_ready: boolean;
    total_documents: number;
    documents_with_kg: number;
    building_documents: string[];
    pending_jobs: string[];
    last_build_time?: string;
  };
}

export interface KGBuildProgress {
  agent_id: string;
  job_id: string;
  progress_percentage: number;
  message: string;
  document_id?: string;
  documents_processed?: number;
  total_documents?: number;
}

export interface KGBuildComplete {
  agent_id: string;
  job_id: string;
  success: boolean;
  total_documents: number;
  documents_processed: number;
  failed_documents: string[];
  message: string;
}

export interface KGBuildError {
  agent_id: string;
  job_id: string;
  error_message: string;
  document_id?: string;
  recoverable: boolean;
}

export interface KGSubscriptionConfirm {
  agent_id: string;
  user_id: string;
  subscribed: boolean;
  message: string;
}

export interface KGBuildStarted {
  agent_id: string;
  job_id: string;
  document_ids: string[];
  force_rebuild: boolean;
  message: string;
}

interface IKGSocketContext {
  socket: Socket | null;
  kgState: {
    subscribedAgents: Set<string>;
    agentStatuses: Record<string, KGAgentStatusUpdate['status']>;
    buildProgresses: Record<string, KGBuildProgress>;
    buildStatuses: Record<string, KGBuildStatus>;
    isSubscribing: boolean;
    subscriptionError: string | null;
  };
  kgActions: {
    subscribeToAgent: (agentId: string) => Promise<boolean>;
    unsubscribeFromAgent: (agentId: string) => Promise<boolean>;
    startAgentKGBuild: (agentId: string, options?: { force_rebuild?: boolean; document_ids?: string[] }) => Promise<boolean>;
    requestAgentStatus: (agentId: string, options?: { include_folders?: boolean }) => Promise<boolean>;
  };
}

const KGSocketContext = createContext<IKGSocketContext>({
  socket: null,
  kgState: {
    subscribedAgents: new Set(),
    agentStatuses: {},
    buildProgresses: {},
    buildStatuses: {},
    isSubscribing: false,
    subscriptionError: null
  },
  kgActions: {
    subscribeToAgent: async () => false,
    unsubscribeFromAgent: async () => false,
    startAgentKGBuild: async () => false,
    requestAgentStatus: async () => false
  }
});

export const useKGSocket = () => useContext(KGSocketContext);

export const KGSocketProvider = ({ children }: { children: ReactNode }) => {
  const { socket } = useAdminSocket();

  const [subscribedAgents, setSubscribedAgents] = useState<Set<string>>(new Set());
  const [agentStatuses, setAgentStatuses] = useState<Record<string, KGAgentStatusUpdate['status']>>({});
  const [buildProgresses, setBuildProgresses] = useState<Record<string, KGBuildProgress>>({});
  const [buildStatuses, setBuildStatuses] = useState<Record<string, KGBuildStatus>>({});
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  const subscribedAgentsRef = useRef<Set<string>>(new Set());
  useEffect(() => { subscribedAgentsRef.current = subscribedAgents; }, [subscribedAgents]);

  useEffect(() => {
    if (!socket) return;

    const handleAgentStatusUpdate = (data: KGAgentStatusUpdate) => {
      setAgentStatuses((prev) => ({ ...prev, [data.agent_id]: data.status }));
    };

    const handleBuildProgress = (data: KGBuildProgress) => {
      setBuildProgresses((prev) => ({ ...prev, [data.job_id]: data }));
      if (data.document_id) {
        const docId = data.document_id;
        setBuildStatuses((prev) => ({
          ...prev,
          [docId]: {
            document_id: docId,
            status: 'running',
            message: data.message,
            started_at: prev[docId]?.started_at || new Date().toISOString()
          }
        }));
      }
    };

    const handleBuildComplete = (data: KGBuildComplete) => {
      setBuildProgresses((prev) => {
        const updated = { ...prev };
        delete updated[data.job_id];
        return updated;
      });

      const status = data.success ? 'completed' : 'failed';
      const completedDocuments = data.total_documents - data.failed_documents.length;

      setBuildStatuses((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((docId) => {
          if (updated[docId].message?.includes(data.job_id)) {
            updated[docId] = {
              ...updated[docId],
              status,
              progress: 100,
              message: data.message,
              completed_at: new Date().toISOString(),
              ...(data.failed_documents.includes(docId) && { error: 'Build failed for this document' })
            };
          }
        });
        return updated;
      });

      setAgentStatuses((prev) => {
        const currentStatus = prev[data.agent_id];
        if (currentStatus) {
          return {
            ...prev,
            [data.agent_id]: {
              ...currentStatus,
              documents_with_kg: currentStatus.documents_with_kg + completedDocuments,
              building_documents: currentStatus.building_documents.filter((docId) => !data.failed_documents.includes(docId))
            }
          };
        }
        return prev;
      });
    };

    const handleBuildError = (data: KGBuildError) => {
      console.error('[KG Socket] Build error:', data);
      setBuildProgresses((prev) => {
        const updated = { ...prev };
        delete updated[data.job_id];
        return updated;
      });

      if (data.document_id) {
        const docId = data.document_id;
        setBuildStatuses((prev) => ({
          ...prev,
          [docId]: {
            document_id: docId,
            status: 'failed',
            progress: 0,
            message: data.error_message,
            error: data.error_message,
            completed_at: new Date().toISOString()
          }
        }));
      }

      setAgentStatuses((prev) => {
        const currentStatus = prev[data.agent_id];
        if (currentStatus && data.document_id) {
          return {
            ...prev,
            [data.agent_id]: {
              ...currentStatus,
              building_documents: currentStatus.building_documents.filter((docId) => docId !== data.document_id)
            }
          };
        }
        return prev;
      });
    };

    const handleSubscribed = (data: KGSubscriptionConfirm) => {
      if (data.subscribed) {
        setSubscribedAgents((prev) => new Set([...prev, data.agent_id]));
        setSubscriptionError(null);
      } else {
        setSubscriptionError(data.message || 'Failed to subscribe to agent updates');
      }
      setIsSubscribing(false);
    };

    const handleBuildStarted = (data: KGBuildStarted) => {
      const initialStatuses: Record<string, KGBuildStatus> = {};
      data.document_ids.forEach((docId) => {
        initialStatuses[docId] = {
          document_id: docId,
          status: 'pending',
          progress: 0,
          message: `Build started (Job: ${data.job_id})`,
          started_at: new Date().toISOString()
        };
      });

      setBuildStatuses((prev) => ({ ...prev, ...initialStatuses }));

      setAgentStatuses((prev) => {
        const currentStatus = prev[data.agent_id];
        if (currentStatus) {
          return {
            ...prev,
            [data.agent_id]: {
              ...currentStatus,
              building_documents: [...new Set([...currentStatus.building_documents, ...data.document_ids])],
              pending_jobs: [...new Set([...currentStatus.pending_jobs, data.job_id])]
            }
          };
        }
        return prev;
      });
    };

    socket.on('agent_kg_status_update', handleAgentStatusUpdate);
    socket.on('agent_kg_build_progress', handleBuildProgress);
    socket.on('agent_kg_build_complete', handleBuildComplete);
    socket.on('agent_kg_build_error', handleBuildError);
    socket.on('agent_kg_subscribed', handleSubscribed);
    socket.on('agent_kg_build_started', handleBuildStarted);

    return () => {
      socket.off('agent_kg_status_update', handleAgentStatusUpdate);
      socket.off('agent_kg_build_progress', handleBuildProgress);
      socket.off('agent_kg_build_complete', handleBuildComplete);
      socket.off('agent_kg_build_error', handleBuildError);
      socket.off('agent_kg_subscribed', handleSubscribed);
      socket.off('agent_kg_build_started', handleBuildStarted);
    };
  }, [socket]);

  const subscribeToAgent = useCallback(
    async (agentId: string): Promise<boolean> => {
      if (!socket?.connected) {
        setSubscriptionError('Socket not connected');
        return false;
      }
      if (subscribedAgentsRef.current.has(agentId)) return true;

      try {
        setIsSubscribing(true);
        setSubscriptionError(null);
        socket.emit('agent_kg_subscribe', { agent_id: agentId });
        return true;
      } catch (error) {
        setSubscriptionError(error instanceof Error ? error.message : 'Subscription failed');
        setIsSubscribing(false);
        return false;
      }
    },
    [socket]
  );

  const unsubscribeFromAgent = useCallback(
    async (agentId: string): Promise<boolean> => {
      if (!socket?.connected) return false;
      if (!subscribedAgentsRef.current.has(agentId)) return true;

      try {
        socket.emit('agent_kg_unsubscribe', { agent_id: agentId });
        setSubscribedAgents((prev) => {
          const updated = new Set(prev);
          updated.delete(agentId);
          return updated;
        });
        setAgentStatuses((prev) => {
          const updated = { ...prev };
          delete updated[agentId];
          return updated;
        });
        setBuildProgresses((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((jobId) => {
            if (updated[jobId].agent_id === agentId) delete updated[jobId];
          });
          return updated;
        });
        return true;
      } catch (error) {
        console.error('[KG] Error unsubscribing from agent:', error);
        return false;
      }
    },
    [socket]
  );

  const startAgentKGBuild = useCallback(
    async (agentId: string, options: { force_rebuild?: boolean; document_ids?: string[] } = {}): Promise<boolean> => {
      if (!socket?.connected) return false;
      try {
        socket.emit('agent_kg_build_start', {
          agent_id: agentId,
          force_rebuild: options.force_rebuild || false,
          ...(options.document_ids && { document_ids: options.document_ids })
        });
        return true;
      } catch (error) {
        console.error('[KG] Error starting build:', error);
        return false;
      }
    },
    [socket]
  );

  const requestAgentStatus = useCallback(
    async (agentId: string, options: { include_folders?: boolean } = {}): Promise<boolean> => {
      if (!socket?.connected) return false;
      try {
        socket.emit('agent_kg_status_request', { agent_id: agentId, include_folders: options.include_folders || false });
        return true;
      } catch (error) {
        console.error('[KG] Error requesting status:', error);
        return false;
      }
    },
    [socket]
  );

  const kgState = useMemo(
    () => ({ subscribedAgents, agentStatuses, buildProgresses, buildStatuses, isSubscribing, subscriptionError }),
    [subscribedAgents, agentStatuses, buildProgresses, buildStatuses, isSubscribing, subscriptionError]
  );

  const kgActions = useMemo(
    () => ({ subscribeToAgent, unsubscribeFromAgent, startAgentKGBuild, requestAgentStatus }),
    [subscribeToAgent, unsubscribeFromAgent, startAgentKGBuild, requestAgentStatus]
  );

  return (
    <KGSocketContext.Provider value={{ socket, kgState, kgActions }}>
      {children}
    </KGSocketContext.Provider>
  );
};
